/**
 * The screen an engineer looks at in the car park.
 *
 * It answers one question — *if I walk in there now and lose signal, will my
 * work survive?* — and it is allowed to answer no. A readiness screen that
 * always goes green is decoration, so the stops are loud, sorted first, and
 * each one carries the thing to do about it.
 */

import type { Readiness, Severity } from '../domain/readiness.js';

const LABEL: Record<Severity, string> = { stop: 'Stop', warn: 'Watch', ok: 'Ready' };

export interface ReadinessViewProps {
  readiness: Readiness;
  onRecheck?: (() => void) | undefined;
  onDismiss?: (() => void) | undefined;
}

export function ReadinessView({ readiness, onRecheck, onDismiss }: ReadinessViewProps) {
  const { verdict, findings, summary } = readiness;
  return (
    <section className={`readiness readiness-${verdict}`} data-testid="readiness" aria-label="Offline readiness">
      <header className="readiness-head">
        <span className={`pill pill-${verdict}`} data-testid="readiness-verdict">
          {LABEL[verdict]}
        </span>
        <p className="readiness-summary">{summary}</p>
      </header>

      <ul className="readiness-list">
        {findings.map((f) => (
          <li key={f.id} className={`readiness-item readiness-${f.severity}`} data-testid={`finding-${f.id}`}>
            <span className={`dot dot-${f.severity}`} aria-hidden="true" />
            <div>
              <strong>{f.title}</strong>
              <p>{f.detail}</p>
              {f.fix ? <p className="readiness-fix">{f.fix}</p> : null}
            </div>
          </li>
        ))}
      </ul>

      <div className="readiness-actions">
        {onRecheck ? (
          <button type="button" onClick={onRecheck} data-testid="recheck">
            Check again
          </button>
        ) : null}
        {onDismiss ? (
          <button type="button" className="ghost" onClick={onDismiss} data-testid="readiness-continue">
            {/* Never "Ignore". The engineer may have a reason to go in anyway —
                a two-photo visit on a half-full phone — but the wording should
                not pretend the findings went away. */}
            {verdict === 'stop' ? 'Go in anyway' : 'Continue'}
          </button>
        ) : null}
      </div>
    </section>
  );
}
